import React, { useRef, useEffect } from "react";
import "./ExpandedChatOverlay.css";

/// コメントデータの型定義
interface CommentData {
  id: string;
  userId: string; // ユーザー固有ID（ブラウザ固有）
  username: string;
  text: string;
  timestamp: number;
  color?: string;
}

/// ExpandedChatOverlayのProps型定義
interface ExpandedChatOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  comments: CommentData[];
  commentText: string;
  onCommentTextChange: (text: string) => void;
  onCommentSubmit: (e: React.FormEvent) => void;
  username: string;
  onUsernameChange: (username: string) => void;
  userId: string; // ユーザー固有ID
  shouldAutoScroll: boolean;
  showScrollToBottom: boolean;
  onScrollToBottom: () => void;
  onChatScroll: (e: React.UIEvent<HTMLDivElement>) => void;
  onTouchStart: (e: React.TouchEvent<HTMLDivElement>) => void;
  onTouchEnd: (e: React.TouchEvent<HTMLDivElement>) => void;
}

/// コメントタイムスタンプのフォーマット関数
const formatCommentTimestamp = (timestamp: number): string => {
  const now = new Date();
  const commentDate = new Date(timestamp);

  // 今日の開始時刻（00:00:00）を取得
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  // 昨日の開始時刻（00:00:00）を取得
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  if (commentDate >= today) {
    // 今日のコメント：時分のみ表示
    return commentDate.toLocaleTimeString("ja-JP", {
      hour: "2-digit",
      minute: "2-digit",
    });
  } else if (commentDate >= yesterday) {
    // 昨日のコメント：「昨日 HH:MM」形式
    return `昨日 ${commentDate.toLocaleTimeString("ja-JP", {
      hour: "2-digit",
      minute: "2-digit",
    })}`;
  } else {
    // 昨日以前のコメント：月日と時分を表示
    return commentDate.toLocaleString("ja-JP", {
      month: "numeric",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }
};

const ExpandedChatOverlay: React.FC<ExpandedChatOverlayProps> = ({
  isOpen,
  onClose,
  comments,
  commentText,
  onCommentTextChange,
  onCommentSubmit,
  username,
  onUsernameChange,
  userId,
  shouldAutoScroll,
  showScrollToBottom,
  onScrollToBottom,
  onChatScroll,
  onTouchStart,
  onTouchEnd,
}) => {
  const chatMessagesRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  /// オーバーレイ外側のクリック処理
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  /// ESCキーでオーバーレイを閉じる
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      // スクロールを無効化
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose]);

  /// 自動スクロール処理
  useEffect(() => {
    if (shouldAutoScroll && chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  }, [comments, shouldAutoScroll]);

  return (
    <div
      className={`chat-overlay ${isOpen ? "chat-overlay--open" : ""}`}
      ref={overlayRef}
    >
      {/* 背景オーバーレイ */}
      <div className="chat-overlay__backdrop" onClick={handleBackdropClick} />
      
      {/* チャットコンテンツ */}
      <div className="chat-overlay__content">
        {/* ヘッダー */}
        <div className="chat-overlay__header">
          <h3 className="chat-overlay__title">ライブチャット</h3>
          <button 
            className="chat-overlay__close-button"
            onClick={onClose}
            type="button"
            aria-label="チャットを閉じる"
          >
            ✕
          </button>
        </div>

        {/* ユーザー名入力 */}
        <div className="chat-overlay__username-section">
          <input
            type="text"
            value={username}
            onChange={(e) => onUsernameChange(e.target.value)}
            placeholder="ユーザー名を入力"
            className="chat-overlay__username-input"
            maxLength={20}
          />
        </div>

        {/* チャットメッセージ */}
        <div
          className="chat-overlay__messages"
          ref={chatMessagesRef}
          onScroll={onChatScroll}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          {comments.length === 0 ? (
            <div className="chat-overlay__empty">
              <p>💬 コメントがありません</p>
              <p>最初のコメントを投稿してみましょう！</p>
            </div>
          ) : (
            comments.slice(-50).map((comment) => (
              <div key={comment.id} className="chat-overlay__message">
                <span 
                  className={`chat-overlay__message-username ${
                    comment.userId === userId ? 'chat-overlay__message-username--own' : 'chat-overlay__message-username--other'
                  }`}
                >
                  {comment.username}:
                </span>
                <span className="chat-overlay__message-content">{comment.text}</span>
                <span className="chat-overlay__message-timestamp">
                  {formatCommentTimestamp(comment.timestamp)}
                </span>
              </div>
            ))
          )}

          {/* 最下部へスクロールボタン */}
          {showScrollToBottom && (
            <button
              className="chat-overlay__scroll-to-bottom"
              onClick={onScrollToBottom}
              type="button"
              title="最新のコメントへ"
            >
              最新
            </button>
          )}
        </div>

        {/* チャット入力 */}
        <form
          onSubmit={onCommentSubmit}
          className="chat-overlay__input-form"
        >
          <input
            type="text"
            value={commentText}
            onChange={(e) => onCommentTextChange(e.target.value)}
            placeholder="コメントを入力..."
            className="chat-overlay__input"
            maxLength={100}
            disabled={!username.trim()}
          />
          <button
            type="submit"
            disabled={!commentText.trim() || !username.trim()}
            className="chat-overlay__send-button"
            aria-label="コメントを送信"
          >
            送信
          </button>
        </form>
      </div>
    </div>
  );
};

export default ExpandedChatOverlay; 